import { useRef, useCallback, useEffect } from 'react';

// Lo-fi chiptune music generator for the trading floor
// Uses Web Audio API to create a pleasant ambient soundtrack

// Pentatonic scale notes (C major pentatonic) — always sounds good
const PENTATONIC = [261.63, 293.66, 329.63, 392.00, 440.00]; // C4, D4, E4, G4, A4
const BASS_NOTES = [130.81, 146.83, 164.81, 196.00]; // C3, D3, E3, G3
const CHORD_ROOTS = [
  [261.63, 329.63, 392.00],  // C major
  [220.00, 261.63, 329.63],  // A minor
  [293.66, 349.23, 440.00],  // D minor
  [196.00, 246.94, 293.66],  // G major
];

interface MusicState {
  ctx: AudioContext;
  masterGain: GainNode;
  playing: boolean;
  nextBeat: number;
  beatIndex: number;
  chordIndex: number;
  melodyTimeout: number | null;
  bassTimeout: number | null;
  chordTimeout: number | null;
  drumTimeout: number | null;
}

function createNote(
  ctx: AudioContext,
  dest: AudioNode,
  freq: number,
  type: OscillatorType,
  startTime: number,
  duration: number,
  volume: number = 0.1,
) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, startTime);
  gain.gain.setValueAtTime(0, startTime);
  gain.gain.linearRampToValueAtTime(volume, startTime + 0.02);
  gain.gain.setValueAtTime(volume, startTime + duration * 0.6);
  gain.gain.linearRampToValueAtTime(0, startTime + duration);
  osc.connect(gain);
  gain.connect(dest);
  osc.start(startTime);
  osc.stop(startTime + duration + 0.05);
}

function createDrum(
  ctx: AudioContext,
  dest: AudioNode,
  startTime: number,
  isKick: boolean,
) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  if (isKick) {
    osc.frequency.setValueAtTime(150, startTime);
    osc.frequency.exponentialRampToValueAtTime(40, startTime + 0.12);
    gain.gain.setValueAtTime(0.15, startTime);
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.15);
    osc.start(startTime);
    osc.stop(startTime + 0.2);
  } else {
    // Hi-hat using noise-like high freq
    osc.frequency.setValueAtTime(800, startTime);
    gain.gain.setValueAtTime(0.04, startTime);
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.06);
    osc.start(startTime);
    osc.stop(startTime + 0.1);
  }
  osc.connect(gain);
  gain.connect(dest);
}

function scheduleBeat(state: MusicState) {
  if (!state.playing) return;

  const ctx = state.ctx;
  const now = ctx.currentTime;
  const beatDuration = 0.35; // ~85 BPM, chill tempo
  const startTime = Math.max(now, state.nextBeat);

  // --- Chord pad (every 8 beats, sustain for 8 beats) ---
  if (state.beatIndex % 8 === 0) {
    const chord = CHORD_ROOTS[state.chordIndex % CHORD_ROOTS.length];
    for (const freq of chord) {
      createNote(ctx, state.masterGain, freq * 0.5, 'triangle', startTime, beatDuration * 7.5, 0.04);
    }
    state.chordIndex++;
  }

  // --- Bass (every 4 beats) ---
  if (state.beatIndex % 4 === 0) {
    const bassNote = BASS_NOTES[state.chordIndex % BASS_NOTES.length];
    createNote(ctx, state.masterGain, bassNote, 'triangle', startTime, beatDuration * 3.5, 0.08);
  }

  // --- Melody (random pentatonic notes, not every beat) ---
  if (state.beatIndex % 2 === 0 || Math.random() > 0.6) {
    const noteIdx = Math.floor(Math.random() * PENTATONIC.length);
    const octave = Math.random() > 0.7 ? 2 : 1; // occasionally go up an octave
    createNote(
      ctx, state.masterGain,
      PENTATONIC[noteIdx] * octave,
      'square',
      startTime + (Math.random() * 0.05), // slight humanize
      beatDuration * (0.5 + Math.random() * 0.5),
      0.035,
    );
  }

  // --- Drums (lo-fi: kick on 1&3, hihat on all) ---
  const beatInBar = state.beatIndex % 4;
  if (beatInBar === 0 || beatInBar === 2) {
    createDrum(ctx, state.masterGain, startTime, true);
  }
  createDrum(ctx, state.masterGain, startTime + 0.01, false);

  state.beatIndex++;
  state.nextBeat = startTime + beatDuration;

  // Schedule next beat
  const delay = (state.nextBeat - ctx.currentTime) * 1000 - 50; // 50ms lookahead
  state.melodyTimeout = window.setTimeout(() => scheduleBeat(state), Math.max(10, delay));
}

export function useFloorMusic() {
  const stateRef = useRef<MusicState | null>(null);
  const isPlayingRef = useRef(false);

  const toggle = useCallback(() => {
    if (isPlayingRef.current) {
      // Stop
      const s = stateRef.current;
      if (s) {
        s.playing = false;
        isPlayingRef.current = false;
        if (s.melodyTimeout) clearTimeout(s.melodyTimeout);
        // Fade out
        s.masterGain.gain.linearRampToValueAtTime(0, s.ctx.currentTime + 0.5);
        setTimeout(() => {
          s.ctx.suspend();
        }, 600);
      }
      return false;
    } else {
      // Start
      let s = stateRef.current;
      if (!s) {
        const ctx = new AudioContext();
        const masterGain = ctx.createGain();
        masterGain.gain.setValueAtTime(0.6, ctx.currentTime);
        masterGain.connect(ctx.destination);
        s = {
          ctx,
          masterGain,
          playing: false,
          nextBeat: 0,
          beatIndex: 0,
          chordIndex: 0,
          melodyTimeout: null,
          bassTimeout: null,
          chordTimeout: null,
          drumTimeout: null,
        };
        stateRef.current = s;
      } else {
        s.ctx.resume();
        s.masterGain.gain.setValueAtTime(0.6, s.ctx.currentTime);
      }
      s.playing = true;
      s.nextBeat = s.ctx.currentTime;
      isPlayingRef.current = true;
      scheduleBeat(s);
      return true;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const s = stateRef.current;
      if (s) {
        s.playing = false;
        if (s.melodyTimeout) clearTimeout(s.melodyTimeout);
        s.ctx.close();
      }
    };
  }, []);

  return { toggle, isPlayingRef };
}
